import React from 'react';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import AITimeSeriesChart from '../aiInsights/charts/AITimeSeriesChart';
import AIDistributionChart from '../aiInsights/charts/AIDistributionChart';
import AIPercentileChart from '../aiInsights/charts/AIPercentileChart';
import AIDataTable from '../aiInsights/charts/AIDataTable';
import '../assets/FleetAnalytics.css';

const DURATIONS = [
    { key: '1d',  label: 'D' },
    { key: '7d',  label: 'W' },
    { key: '30d', label: 'M', disabled: true },
];

const durationToAIDays = (key) => key === '1d' ? 1 : key === '7d' ? 7 : 30;

// ── Render helpers (same as FleetAnalyticsDashboard) ──

function renderMetricsCard(card, icon, title, iconColor) {
    if (!card || !card.primary_metric || !card.secondary_metric || !card.range_metric || !card.count_metric) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className={`ai-card-icon ${iconColor}`}>{icon}</div>
                <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-metrics-stats">
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.primary_metric.color}`}>{card.primary_metric.value}</span>
                    <span className="ai-stat-label">{card.primary_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.secondary_metric.color}`}>{card.secondary_metric.value}</span>
                    <span className="ai-stat-label">{card.secondary_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.range_metric.color}`}>{card.range_metric.value}</span>
                    <span className="ai-stat-label">{card.range_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.count_metric.color}`}>{card.count_metric.value}</span>
                    <span className="ai-stat-label">{card.count_metric.label}</span>
                </div>
            </div>
        </div>
    );
}

function renderTrendsCard(card) {
    if (!card) return null;
    const { chart_type, chart_data, chart_labels, table_data, icon, title, trend_description, details } = card;
    const header = (
        <div className="ai-card-header">
            <div className="ai-card-icon primary-bg">{icon}</div>
            <div className="ai-card-title">{title}</div>
        </div>
    );
    const desc = (
        <>
            <div className="ai-trend-description"><strong>Trend:</strong> {trend_description}</div>
            <div className="ai-trend-details">{details}</div>
        </>
    );
    switch (chart_type) {
        case 'time-series':
            return <div className="ai-card">{header}{desc}<AITimeSeriesChart title="" chartData={chart_data} chartLabels={chart_labels} yAxisLabel="Value" /></div>;
        case 'distribution':
            return <div className="ai-card">{header}{desc}<AIDistributionChart title="" chartData={chart_data} chartType="auto" /></div>;
        case 'percentile':
            return <div className="ai-card">{header}{desc}<AIPercentileChart title="" chartData={chart_data} /></div>;
        case 'table':
            return <div className="ai-card">{header}{desc}<AIDataTable title="" tableData={table_data} /></div>;
        case 'none':
            return <div className="ai-card">{header}{desc}</div>;
        default: {
            const hasData = chart_data && Array.isArray(chart_data) && chart_data.length > 0;
            const isOldFormat = hasData && typeof chart_data[0] === 'number';
            if (isOldFormat) {
                const maxVal = Math.max(...chart_data);
                return (
                    <div className="ai-card">{header}{desc}
                        <div className="ai-chart-container">
                            {chart_data.slice(0, 100).map((v, i) => (
                                <div key={i} className="ai-chart-bar" style={{ height: `${(v / maxVal) * 150}px` }} title={`${v}`}></div>
                            ))}
                        </div>
                    </div>
                );
            }
            return <div className="ai-card">{header}{desc}<AITimeSeriesChart title="" chartData={chart_data} chartLabels={chart_labels} yAxisLabel="Value" /></div>;
        }
    }
}

function renderPatternsCard(card) {
    if (!card || !card.items) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon warning-bg">{card.icon}</div>
                <div className="ai-card-title">{card.title}</div>
            </div>
            <ul className="ai-item-list">
                {card.items.map((item, i) => (
                    <li key={i} className="ai-item">
                        <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
                        <div className="ai-item-content">
                            <div className="ai-item-label">{item.label}<span className="ai-item-value">{item.value}</span></div>
                            <div className="ai-item-details">{item.description}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function renderCacheInfo(metadata, deviceCount) {
    if (!metadata && deviceCount == null) return null;
    const deviceChip = deviceCount != null ? (
        <span className="ai-device-count-chip">{deviceCount} device{deviceCount !== 1 ? 's' : ''}</span>
    ) : null;
    if (!metadata) return deviceChip ? <div className="ai-cache-info"><div className="ai-cache-info-content"><div className="ai-cache-info-left">{deviceChip}</div></div></div> : null;
    const { cached_at, data_time_range, is_stale, status, warning } = metadata;
    const formatRelative = (ts) => {
        if (!ts) return '';
        const diff = Date.now() - new Date(ts).getTime();
        const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
        if (m < 1) return 'just now';
        if (m < 60) return `${m} minute${m !== 1 ? 's' : ''} ago`;
        if (h < 24) return `${h} hour${h !== 1 ? 's' : ''} ago`;
        return `${d} day${d !== 1 ? 's' : ''} ago`;
    };
    const formatRange = (r) => {
        if (!r || !r.from || !r.to) return '';
        const opts = { month: 'short', day: 'numeric', year: 'numeric' };
        return `${new Date(r.from).toLocaleDateString('en-US', opts)} - ${new Date(r.to).toLocaleDateString('en-US', opts)}`;
    };
    return (
        <div className={`ai-cache-info${is_stale ? ' stale' : ''}`}>
            <div className="ai-cache-info-content">
                <div className="ai-cache-info-left">
                    <span className={`ai-cache-status-dot ${status === 'ready' ? 'ready' : 'warning'}`}></span>
                    <span className="ai-cache-info-text"><strong>Last Updated:</strong> {formatRelative(cached_at)}</span>
                    {data_time_range && <span className="ai-cache-info-text ai-cache-info-separator"><strong>Data Range:</strong> {formatRange(data_time_range)}</span>}
                    {deviceChip}
                </div>
                {warning && <div className="ai-cache-info-right"><span className="ai-cache-warning">⚠️ {warning}</span></div>}
            </div>
        </div>
    );
}

function AIErrorCard({ error, onRetry }) {
    if (!error) return null;
    return (
        <div className="ai-error-card" style={{ marginBottom: 12 }}>
            <div className="ai-error-icon">
                {error.type === 'warming' ? '⏳' : error.type === 'not_found' ? '🔍' : '⚠️'}
            </div>
            <h3>{error.type === 'warming' ? 'Generating Insights…' : error.type === 'not_found' ? 'Data Not Found' : error.type === 'timeout' ? 'Request Timed Out' : 'Error Loading Insights'}</h3>
            <p>{error.message}</p>
            {onRetry && error.type !== 'warming' && (
                <button className="ai-retry-btn" onClick={onRetry}>Retry</button>
            )}
        </div>
    );
}

// ── Main component ──
function CrashAnalytics() {
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('1d');
    const [viewType, setViewType]   = React.useState('aggregate');
    const [selectedTag, setSelectedTag]   = React.useState(null);
    const [availableTags, setAvailableTags] = React.useState([]);
    const [tagsLoading, setTagsLoading]   = React.useState(false);

    const [crashData, setCrashData]     = React.useState(null);
    const [crashLoading, setCrashLoading] = React.useState(false);
    const [crashError, setCrashError]   = React.useState(null);
    const [cacheMetadata, setCacheMetadata] = React.useState(null);
    const [deviceCount, setDeviceCount] = React.useState(null);
    const [retryAttempts, setRetryAttempts] = React.useState(0);
    const apigeeTokenRef = React.useRef(null);

    const MAX_RETRY = 3;

    const buildUrl = React.useCallback((vt, tag) => {
        const base = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
        const ver  = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
        const days = durationToAIDays(selectedTimeRange);
        if (vt === 'device-tag' && tag) {
            return `${base}/api/${ver}/insights/device-tag/${tag}/crashes?days=${days}`;
        }
        return `${base}/api/${ver}/insights/aggregate/crashes?days=${days}`;
    }, [selectedTimeRange]);

    const fetchCrash = React.useCallback(async (vt, tag, attempt, token) => {
        try {
            setCrashLoading(true);
            setCrashError(null);
            const url = buildUrl(vt, tag);
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const response = await fetch(url, { headers });
            switch (response.status) {
                case 200: {
                    const d = await response.json();
                    setCrashData(d.insights);
                    if (d.device_count != null) setDeviceCount(d.device_count);
                    if (d.cache_metadata) setCacheMetadata(d.cache_metadata);
                    setRetryAttempts(0);
                    break;
                }
                case 202: {
                    const d = await response.json();
                    if (attempt < MAX_RETRY) {
                        const after = d.retry_after || 30;
                        setCrashError({ type: 'warming', message: `${d.message} (Attempt ${attempt + 1}/${MAX_RETRY})`, retryAfter: after });
                        setRetryAttempts(attempt + 1);
                        setTimeout(() => fetchCrash(vt, tag, attempt + 1, token), after * 1000);
                    } else {
                        setCrashError({ type: 'timeout', message: 'Insights generation timed out. Please try again later.' });
                        setRetryAttempts(0);
                    }
                    break;
                }
                case 404: {
                    const d = await response.json();
                    setCrashError({ type: 'not_found', message: d.message || 'Resource not found' });
                    setRetryAttempts(0);
                    break;
                }
                case 500: {
                    const d = await response.json();
                    setCrashError({ type: 'failed', message: d.message || 'Failed to generate insights' });
                    setRetryAttempts(0);
                    break;
                }
                default:
                    setCrashError({ type: 'unexpected', message: `Unexpected error (HTTP ${response.status}).` });
                    setRetryAttempts(0);
            }
        } catch {
            setCrashError({ type: 'network', message: 'Network error. Please check your connection.' });
            setRetryAttempts(0);
        } finally {
            setCrashLoading(false);
        }
    }, [buildUrl]);

    const load = React.useCallback(async (vt, tag) => {
        setCrashData(null);
        setCrashError(null);
        setDeviceCount(null);
        setCacheMetadata(null);
        let token = apigeeTokenRef.current;
        if (!token) {
            try {
                const tokenUrl = (process.env.REACT_APP_WBC_API_BASE_URL || '') + '/api/v1/apigee/token';
                const res = await fetch(tokenUrl);
                if (res.ok) { const d = await res.json(); token = d.access_token || null; }
            } catch { /* proceed without token */ }
            apigeeTokenRef.current = token;
        }
        fetchCrash(vt, tag, retryAttempts, token);
    }, [fetchCrash, retryAttempts]);

    React.useEffect(() => {
        if (viewType === 'aggregate') {
            load('aggregate', null);
        } else if (viewType === 'device-tag' && selectedTag) {
            load('device-tag', selectedTag);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimeRange, viewType, selectedTag]);

    React.useEffect(() => {
        if (viewType !== 'device-tag' || availableTags.length > 0) return;
        const apiBase = (process.env.REACT_APP_WBC_API_BASE_URL || '').replace(/\/$/, '');
        const qpath   = process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1';
        setTagsLoading(true);
        fetch(`${apiBase}${qpath.startsWith('/') ? qpath : '/' + qpath}/aggregates/tags`)
            .then(r => r.json())
            .then(d => setAvailableTags(d.tags || []))
            .catch(() => setAvailableTags([]))
            .finally(() => setTagsLoading(false));
    }, [viewType, availableTags.length]);

    const handleViewTypeChange = (vt) => {
        if (vt === viewType) return;
        setCrashData(null); setCrashError(null);
        setViewType(vt);
    };

    const controls = (
        <div className="page-intro">
            <div className="duration-bar">
                <span className="duration-label">Duration:</span>
                {DURATIONS.map(d => {
                    const chip = (
                        <span
                            className={`filter-chip${selectedTimeRange === d.key ? ' active' : ''}${d.disabled ? ' disabled' : ''}`}
                            onClick={() => !d.disabled && setSelectedTimeRange(d.key)}
                        >
                            {d.label}
                        </span>
                    );

                    // The disabled chip ignores pointer events, so its wrapper owns the tooltip.
                    return d.disabled ? (
                        <span key={d.key} title="Month view is not currently available.">{chip}</span>
                    ) : React.cloneElement(chip, { key: d.key });
                })}
                <span className="duration-divider" />
                <span className="duration-label">View:</span>
                <span
                    className={`filter-chip${viewType === 'aggregate' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('aggregate')}
                >
                    Aggregate
                </span>
                <span
                    className={`filter-chip${viewType === 'device-tag' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('device-tag')}
                >
                    Device Tag
                </span>
            </div>
        </div>
    );

    const retryFn = () => load(viewType, selectedTag);

    const content = (
        <div className="ai-dashboard active">
            {renderCacheInfo(cacheMetadata, deviceCount)}
            {crashError
                ? <AIErrorCard error={crashError} onRetry={retryFn} />
                : crashLoading
                    ? <div className="card fa-card-body"><LoadingSkeleton height="200px" /></div>
                    : crashData
                        ? <div className="ai-metrics-grid">
                            {renderMetricsCard(crashData.metrics_card, '💥', 'Overview', 'danger-bg')}
                            {renderTrendsCard(crashData.trends_card)}
                            {renderPatternsCard(crashData.patterns_card)}
                          </div>
                        : null}
        </div>
    );

    return (
        <div className="fho-dashboard">
            {controls}

            {viewType === 'device-tag' && (
                <div className="tag-selector-row">
                    <span className="duration-label" style={{ marginRight: 8 }}>Device Group:</span>
                    <select
                        className="tag-select"
                        value={selectedTag || ''}
                        onChange={e => setSelectedTag(e.target.value || null)}
                        disabled={tagsLoading}
                    >
                        <option value="">{tagsLoading ? 'Loading groups…' : availableTags.length === 0 ? 'No device groups found' : 'Select a device group…'}</option>
                        {availableTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                    </select>
                </div>
            )}

            {viewType === 'device-tag' && !selectedTag ? (
                <div className="tag-empty-state">
                    <div className="tag-empty-icon">🏷️</div>
                    <div className="tag-empty-text">Select a device group to load crash insights</div>
                </div>
            ) : content}
        </div>
    );
}

export default CrashAnalytics;
